import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ProductComplianceTechnicalHeadingTypeEnum } from '@inter-ikea-kompis/enums';
import type {
	IProduct,
	IProductComplianceTechnical,
	IProductTechnicalInformation,
	ITheme
} from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { Table, Text } from '@ingka/common-styles-webc';

import EnergyFicheCardStyle from './EnergyFicheCardStyle.js';

/**
 * Data-driven fiche card.
 */

export default class EnergyFicheCard extends KompisElement {
	// A product.
	@property({ type: Object, required: true })
	public product!: IProduct;

	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Disables links. This attribute should be set to "true" when in a Kiosk.
	@property({ type: Boolean })
	public disableLinks = false;

	public override render(): RenderResult {
		const tableClassName = Table.classnames({ fullWidth: true });

		return html`
			<style>
				${EnergyFicheCardStyle.getStyle()}
			</style>

			<table class="${tableClassName}">
				<tbody>
					${this.getDataRows()}
				</tbody>
			</table>
		`;
	}

	/**
	 * Retrieves data from complianceTechnical and technicalInformation.
	 */
	private getDataRows(): RenderResult {
		const complianceTechnicalItems = this.getComplianceTechnical();
		const technicalInformationitems =
			this.getFilteredTechnicalInformation(complianceTechnicalItems);
		const bodyTextClass = Text.classnames({ body: { m: true } });

		return [
			...technicalInformationitems.map(
				({ typeName, valueMetric, unitMetric = null }) => html`
					<tr class="${bodyTextClass}">
						<td>${typeName}</td>
						<td>${unitMetric ? `${valueMetric} ${unitMetric}` : valueMetric}</td>
					</tr>
				`
			),
			...complianceTechnicalItems.map(
				({ headingText, text }) => html`
					<tr class="${bodyTextClass}">
						<td>${headingText}</td>
						<td>${text}</td>
					</tr>
				`
			)
		];
	}

	/**
	 * Retreives all objects in complianceTechnical.
	 */
	private getComplianceTechnical(): IProductComplianceTechnical[] {
		return (
			this.product.content?.complianceTechnical?.filter(
				(complianceItem) =>
					complianceItem.headingType !== ProductComplianceTechnicalHeadingTypeEnum.nonRescaled
			) ?? []
		);
	}

	/**
	 * Retreives all objects in technicalInformation which are not already present in complianceTechnical.
	 *
	 * @param complianceTechnicalItems ...
	 */
	private getFilteredTechnicalInformation(
		complianceTechnicalItems: IProductComplianceTechnical[]
	): IProductTechnicalInformation[] {
		// Uses 'toUpperCase' as typeName could have the value 'Brand name', where headingText have 'Brand Name'
		return (
			this.product.content?.technicalInformation?.filter(
				(technicalInformationItem) =>
					!complianceTechnicalItems.some(
						(complianceTechnicalItem) =>
							technicalInformationItem.typeName.toUpperCase() ===
							complianceTechnicalItem.headingText.toUpperCase()
					)
			) ?? []
		);
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-energy-fiche-card': EnergyFicheCard;
	}
}
